if (!customElements.get('rtd-in-person-course')) {
  customElements.define(
    'rtd-in-person-course',
    class RtdInPersonCourse extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.cityTabs = Array.from(this.querySelectorAll('[data-city-tab]'));
        this.locationTabs = Array.from(this.querySelectorAll('[data-location-tab]'));
        this.sessionTabs = Array.from(this.querySelectorAll('[data-session-tab]'));
        this.scheduleSelector = this.querySelector('.rtd-in-person-course__selector--schedule');
        this.cards = Array.from(this.querySelectorAll('[data-offering-card]'));
        this.status = this.querySelector('[data-offering-status]');
        this.section = this.closest('section.rtd-in-person-course') || this;
        this.optionalBlocks = Array.from(this.section?.querySelectorAll('[data-dynamic-offering-block]') || []);
        this.optionalBlocksContainer = this.section?.querySelector('[data-optional-blocks]');
        this.cardTransition = window.RtdOfferingTransition
          ? new window.RtdOfferingTransition(this.section)
          : null;
        this.blockTransitions = new WeakMap();
        this.linkOnlineScheduleMessages();

        if (!this.cityTabs.length || !this.locationTabs.length || !this.sessionTabs.length || !this.cards.length) return;

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));

        if (!this.selectVariantFromUrl()) this.readInitialSelection();
        this.update({ selectFirstAvailable: true, animate: false });
      }

      onClick(event) {
        const cityTab = event.target.closest('[data-city-tab]');
        if (cityTab && this.contains(cityTab)) {
          this.city = cityTab.dataset.cityTab;
          this.location = '';
          this.session = '';
          this.update({ selectFirstAvailable: true });
          return;
        }

        const locationTab = event.target.closest('[data-location-tab]');
        if (locationTab && this.contains(locationTab)) {
          this.location = locationTab.dataset.locationTab;
          this.session = '';
          this.update({ selectFirstAvailable: true });
          return;
        }

        const sessionTab = event.target.closest('[data-session-tab]');
        if (sessionTab && this.contains(sessionTab)) {
          this.session = sessionTab.dataset.sessionTab;
          this.update({ selectFirstAvailable: false });
        }
      }

      linkOnlineScheduleMessages() {
        this.querySelectorAll('[data-online-courses-url]').forEach((message) => {
          const url = new URL(message.dataset.onlineCoursesUrl, window.location.origin);
          if (message.dataset.onlineCourseId) url.searchParams.set('course', message.dataset.onlineCourseId);
          const walker = document.createTreeWalker(message, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) =>
              !node.parentElement.closest('a') && /\bonline\b/i.test(node.textContent)
                ? NodeFilter.FILTER_ACCEPT
                : NodeFilter.FILTER_REJECT,
          });
          const nodes = [];
          while (walker.nextNode()) nodes.push(walker.currentNode);
          nodes.forEach((node) => {
            const fragment = document.createDocumentFragment();
            node.textContent.split(/(\bonline\b)/i).forEach((text, index) => {
              if (index % 2) {
                const link = document.createElement('a');
                link.href = url.href;
                link.textContent = text;
                link.target = '_blank';
                link.rel = 'noopener';
                link.setAttribute('aria-describedby', 'a11y-new-window-message');
                fragment.append(link);
              } else {
                fragment.append(document.createTextNode(text));
              }
            });
            node.replaceWith(fragment);
          });
        });
      }

      onKeydown(event) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        const button = event.target.closest('[data-choice-button]');
        const buttonGroup = button?.closest('[data-choice-tabs]');
        if (!buttonGroup || !this.contains(button)) return;

        const buttons = Array.from(buttonGroup.querySelectorAll('[data-choice-button]')).filter(
          (item) => !item.hidden && !item.disabled
        );
        const currentIndex = buttons.indexOf(button);
        if (currentIndex === -1) return;

        event.preventDefault();
        let nextIndex = currentIndex;

        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % buttons.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = buttons.length - 1;

        buttons[nextIndex].focus();
        buttons[nextIndex].click();
      }

      readInitialSelection() {
        this.city = this.selectedValue(this.cityTabs, 'cityTab');
        this.location = this.selectedValue(this.locationTabs, 'locationTab');
        this.session = this.selectedValue(this.sessionTabs, 'sessionTab');
      }

      update({ selectFirstAvailable, animate = true }) {
        if (selectFirstAvailable && !this.city) {
          this.city = this.firstAvailableValue(this.cityTabs, 'cityTab');
        }

        this.refreshLocationTabs();
        if (selectFirstAvailable && !this.location) {
          this.location = this.firstAvailableValue(this.locationTabs, 'locationTab');
        }

        this.refreshSessionTabs();
        if (selectFirstAvailable && !this.session) {
          this.session = this.firstAvailableValue(this.sessionTabs, 'sessionTab');
        }

        this.syncButtonStates();

        const matchingCards = this.cards.filter(
          (card) =>
            card.dataset.city === this.city &&
            card.dataset.location === this.location &&
            card.dataset.session === this.session
        );
        const selectionChanged =
          matchingCards.length !== this.cards.filter((card) => !card.hidden).length ||
          matchingCards.some((card) => card.hidden);

        this.showSelectedOfferings(matchingCards, { animate: animate && selectionChanged });
        this.publishVariantChange(matchingCards);
        this.updateStatus(matchingCards.length > 0);
      }

      publishVariantChange(cards) {
        const variantId = cards[0]?.dataset.variantId || '';
        this.dataset.selectedVariantId = variantId;
        if (!variantId || variantId === this.selectedVariantId) return;

        this.selectedVariantId = variantId;
        this.dispatchEvent(
          new CustomEvent('rtd:variant-change', {
            bubbles: true,
            detail: { variantId },
          })
        );
      }

      refreshLocationTabs() {
        this.locationTabs.forEach((tab) => {
          const available = Boolean(this.city) && tab.dataset.city === this.city;
          tab.hidden = !available;
          tab.disabled = !available;
        });

        if (!this.isAvailable(this.locationTabs, 'locationTab', this.location)) this.location = '';
      }

      refreshSessionTabs() {
        this.sessionTabs.forEach((tab) => {
          const available =
            Boolean(this.city && this.location) &&
            tab.dataset.city === this.city &&
            tab.dataset.location === this.location;
          tab.hidden = !available;
          tab.disabled = !available;
        });

        if (this.scheduleSelector) {
          this.scheduleSelector.hidden = this.sessionTabs.filter((tab) => !tab.hidden).length < 2;
        }

        if (!this.isAvailable(this.sessionTabs, 'sessionTab', this.session)) this.session = '';
      }

      syncButtonStates() {
        this.setButtonState(this.cityTabs, 'cityTab', this.city);
        this.setButtonState(this.locationTabs, 'locationTab', this.location);
        this.setButtonState(this.sessionTabs, 'sessionTab', this.session);
      }

      setButtonState(buttons, datasetKey, value) {
        buttons.forEach((button) => {
          const selected = !button.hidden && button.dataset[datasetKey] === value;
          button.classList.toggle('is-active', selected);
          button.classList.toggle('button--secondary', !selected);
          button.setAttribute('aria-pressed', String(selected));
          button.tabIndex = selected ? 0 : -1;
        });
      }

      showSelectedOfferings(cards, { animate }) {
        const selectedCardIds = new Set(cards.map((card) => card.dataset.variantId));
        this.hideItems(this.cardTransition, this.cards.filter((card) => !selectedCardIds.has(card.dataset.variantId)));
        this.showItems(this.cardTransition, cards, { animate });

        let hasVisibleOptionalBlock = false;
        this.optionalBlocks.forEach((block) => {
          const contents = Array.from(block.querySelectorAll('[data-offering-block-content]'));
          const selectedContents = contents.filter((content) => selectedCardIds.has(content.dataset.variantId));
          const transition = this.getBlockTransition(block);

          this.hideItems(
            transition,
            contents.filter((content) => !selectedCardIds.has(content.dataset.variantId))
          );

          if (selectedContents.length) {
            block.hidden = false;
            this.showItems(transition, selectedContents, { animate });
            hasVisibleOptionalBlock = true;
          } else {
            block.hidden = true;
          }
        });

        if (this.optionalBlocksContainer) {
          this.optionalBlocksContainer.hidden = !hasVisibleOptionalBlock;
        }
      }

      getBlockTransition(block) {
        if (!this.blockTransitions.has(block)) {
          this.blockTransitions.set(
            block,
            window.RtdOfferingTransition ? new window.RtdOfferingTransition(this.section) : null
          );
        }
        return this.blockTransitions.get(block);
      }

      showItems(transition, items, { animate }) {
        if (transition) {
          transition.show(items, { animate });
          return;
        }
        Array.from(items).forEach((item) => {
          item.hidden = false;
        });
      }

      hideItems(transition, items) {
        if (transition) {
          transition.hide(items);
          return;
        }
        Array.from(items).forEach((item) => {
          item.hidden = true;
        });
      }

      selectedValue(buttons, datasetKey) {
        return buttons.find((button) => button.getAttribute('aria-pressed') === 'true')?.dataset[datasetKey] || '';
      }

      firstAvailableValue(buttons, datasetKey) {
        return buttons.find((button) => !button.hidden && !button.disabled)?.dataset[datasetKey] || '';
      }

      isAvailable(buttons, datasetKey, value) {
        return Boolean(
          value && buttons.some((button) => !button.hidden && !button.disabled && button.dataset[datasetKey] === value)
        );
      }

      selectVariantFromUrl() {
        const variantId = new URLSearchParams(window.location.search).get('variant');
        if (!variantId) return false;

        const card = this.cards.find((item) => item.dataset.variantId === variantId);
        if (!card) return false;

        this.city = card.dataset.city;
        this.location = card.dataset.location;
        this.session = card.dataset.session;
        return true;
      }

      updateStatus(hasMatchingOffering) {
        if (!this.status) return;

        this.status.textContent = hasMatchingOffering
          ? `Showing ${this.city}, ${this.location}, ${this.session}.`
          : 'No offering is available for this selection.';
      }
    }
  );
}

if (!customElements.get('rtd-in-person-details')) {
  customElements.define(
    'rtd-in-person-details',
    class RtdInPersonDetails extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.tabs = Array.from(this.querySelectorAll('[data-detail-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-detail-panel]'));
        this.tabList = this.querySelector('[role="tablist"]');
        this.section = this.closest('section.rtd-in-person-course');
        this.handleVariantChange = this.syncTabs.bind(this);

        if (!this.tabs.length || !this.panels.length) return;

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.addEventListener('shopify:block:select', this.onBlockSelect.bind(this));
        this.section?.addEventListener('rtd:variant-change', this.handleVariantChange);
        this.syncTabs();
      }

      disconnectedCallback() {
        this.section?.removeEventListener('rtd:variant-change', this.handleVariantChange);
      }

      onClick(event) {
        const tab = event.target.closest('[data-detail-tab]');
        if (!tab || !this.contains(tab) || tab.hidden || tab.disabled) return;
        this.selectTab(tab.dataset.detailTab);
      }

      onKeydown(event) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        const tab = event.target.closest('[data-detail-tab]');
        if (!tab || !this.contains(tab)) return;

        const availableTabs = this.tabs.filter((item) => !item.hidden && !item.disabled);
        const currentIndex = availableTabs.indexOf(tab);
        if (currentIndex === -1) return;

        event.preventDefault();
        let nextIndex = currentIndex;

        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + availableTabs.length) % availableTabs.length;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % availableTabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = availableTabs.length - 1;

        availableTabs[nextIndex].focus();
        availableTabs[nextIndex].click();
      }

      onBlockSelect(event) {
        const panel = event.target.closest('[data-detail-panel]');
        if (!panel || !this.contains(panel)) return;

        const tab = this.tabs.find((item) => item.dataset.detailTab === panel.dataset.detailPanel);
        if (tab && !tab.hidden && !tab.disabled) this.selectTab(tab.dataset.detailTab);
      }

      syncTabs() {
        this.tabs.forEach((tab) => {
          const panel = this.panels.find((item) => item.dataset.detailPanel === tab.dataset.detailTab);
          const content = panel?.querySelector('[data-dynamic-offering-block]');
          const available = Boolean(content && !content.hidden);

          tab.hidden = !available;
          tab.disabled = !available;
          if (panel) panel.dataset.detailAvailable = String(available);
        });

        const availableTabs = this.tabs.filter((tab) => !tab.hidden && !tab.disabled);
        this.tabList?.setAttribute('data-visible-tabs', String(availableTabs.length));
        const selectedTab =
          availableTabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || availableTabs[0];

        this.hidden = !selectedTab;
        if (selectedTab) {
          this.selectTab(selectedTab.dataset.detailTab);
          return;
        }

        this.panels.forEach((panel) => {
          panel.hidden = true;
          panel.inert = true;
        });
      }

      selectTab(detailId) {
        const selectedTab = this.tabs.find(
          (tab) => tab.dataset.detailTab === detailId && !tab.hidden && !tab.disabled
        );
        if (!selectedTab) return;

        this.tabs.forEach((tab) => {
          const selected = tab === selectedTab;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          const selected = panel.dataset.detailPanel === detailId && panel.dataset.detailAvailable === 'true';
          panel.hidden = !selected;
          panel.inert = !selected;
        });
      }
    }
  );
}
